import { FileSpreadsheet, GitBranch, Boxes, Cpu, LayoutDashboard, ChevronRight, ShieldCheck } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { Reveal } from "./Reveal";
import { Parallax, DataCard } from "./Decor";
import { Tilt } from "@/components/motion/Tilt";
import { useLang } from "@/i18n/LanguageContext";
import { SectionVideo } from "@/components/motion/SectionVideo";
import { VIDEO_PROOF_SOURCES } from "@/lib/site";

const PIPELINE = [
  { icon: FileSpreadsheet, label: "Legacy Excel configurator", tone: "red", sub: "47 sheets, undocumented" },
  { icon: GitBranch, label: "Dependency graph", tone: "blue", sub: "1,240 formula edges" },
  { icon: Boxes, label: "Canonical input model", tone: "blue", sub: "62 typed inputs" },
  { icon: Cpu, label: "Validated calculation engine", tone: "green", sub: "test suite, parity ok" },
  { icon: LayoutDashboard, label: "Secure admin dashboard", tone: "green", sub: "live, role based" },
];

const toneClass = {
  red: "text-[var(--red)] border-[rgba(255,92,108,0.3)]",
  blue: "text-[var(--blue-bright)] border-[var(--line-strong)]",
  green: "text-[var(--green)] border-[rgba(54,226,160,0.3)]",
};

const METRIC_VALUES = ["1,240", "0", "62", "100%"];
const PROOF_CARD_ICONS = [GitBranch, ShieldCheck, Cpu];

export const VisualProofSection = () => {
  const { t } = useLang();
  const pf = t.proof;
  return (
    <section id="proof" className="relative scroll-mt-24 py-24 lg:py-32 overflow-hidden">
      {/* Ambient backlight + one clean floating chip, parked in the empty band
          beside the left-aligned heading. Never over the case card. */}
      <div className="glow-orb animate-glow pointer-events-none" style={{ width: 500, height: 300, top: -50, left: "26%", background: "var(--blue)", opacity: 0.16 }} />
      <Parallax className="hidden lg:block absolute z-0 right-[4%] top-[64px] pointer-events-none" from={20} to={-16}>
        <DataCard label="status" value="validated" tone="green" className="animate-float" />
      </Parallax>

      <div className="relative z-10 mx-auto max-w-7xl px-5">
        <SectionHeading eyebrow={pf.eyebrow} title={pf.title} intro={pf.intro} testId="proof-heading" />

        <Reveal>
          <div className="mt-12 glass-card rounded-3xl p-6 lg:p-9 relative overflow-hidden" data-testid="proof-card">
            <div className="bg-grid bg-grid-fade absolute inset-0 opacity-40" />
            <span className="scan-line" />
            <div className="relative">
              <div className="flex items-center justify-between mb-7">
                <span className="font-mono text-[11px] text-[var(--muted-2)]">case://manufacturing-configurator</span>
                <span className="inline-flex items-center gap-2 font-mono text-[11px] text-[var(--green)]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[var(--green)] validate-pulse" />
                  <ShieldCheck size={13} /> {pf.validated}
                </span>
              </div>

              {/* Cinematic engineering-proof loop (does not cover metrics/text) */}
              <div className="mb-7">
                <SectionVideo sources={VIDEO_PROOF_SOURCES} poster="/posters/proof.jpg" testId="proof-video" overlayLabels={["Dependency graph mapped", "Calculation parity", "Validation loop"]} />
              </div>

              <div className="grid lg:grid-cols-[repeat(5,1fr)] gap-3 lg:gap-1 items-stretch">
                {PIPELINE.map((stage, i) => (
                  <div key={i} className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3 lg:gap-1">
                    <div className="flex-1 panel rounded-2xl p-4 text-center lg:min-h-[150px] flex flex-col items-center justify-center" data-testid={`proof-stage-${i}`}>
                      <span className={`grid place-items-center h-12 w-12 rounded-xl border bg-white/[0.03] mb-3 ${toneClass[stage.tone]}`}>
                        <stage.icon size={22} />
                      </span>
                      <p className="font-display text-sm font-semibold text-white leading-tight">{stage.label}</p>
                      <p className="mt-1.5 font-mono text-[10px] text-[var(--muted-2)]">{stage.sub}</p>
                    </div>
                    {i < PIPELINE.length - 1 && (
                      <ChevronRight className="mx-auto rotate-90 lg:rotate-0 text-[var(--blue)] shrink-0" size={20} />
                    )}
                  </div>
                ))}
              </div>

              <div className="divider-line my-7" />
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
                {pf.metricsLabels.map((label, i) => (
                  <div key={i} data-testid={`proof-metric-${i}`}>
                    <div className="font-display text-2xl lg:text-3xl font-bold text-gradient metric-glow">{METRIC_VALUES[i]}</div>
                    <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-[var(--muted-2)]">{label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        {/* Three anonymized proof cards (kept inside the same case section) */}
        <Reveal>
          <div className="scene-3d mt-5 grid sm:grid-cols-3 gap-5" data-testid="proof-cards">
            {pf.cards.map((card, i) => {
              const Icon = PROOF_CARD_ICONS[i];
              return (
                <Tilt key={i} className="h-full">
                  <article className="glass-card depth-card sheen rounded-3xl p-6 h-full flex flex-col" data-testid={`proof-card-item-${i}`}>
                    <span className="grid place-items-center h-11 w-11 rounded-2xl border border-[var(--line-strong)] bg-[rgba(61,123,255,0.08)] text-[var(--blue-bright)]">
                      <Icon size={20} />
                    </span>
                    <h3 className="mt-5 font-display text-lg font-semibold text-white leading-snug">{card.title}</h3>
                    <p className="mt-3 text-sm text-white/90 leading-relaxed">{card.desc}</p>
                  </article>
                </Tilt>
              );
            })}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
